import {css, StyleSheet} from 'aphrodite';
import React, {RefObject, useEffect, useRef} from 'react';
import isEqual from 'react-fast-compare';

import {AccessibleGridCell, FullyAccessibleGrid, isAccessibleGridCell} from 'constants/SampleConstants';
import {usePlateMapFromGrid} from 'samples/grids/hooks/usePlateMapFromGrid';
import PlateMapDisplayLayerDropdown from 'samples/plate-maps/display-layer-controls/PlateMapDisplayLayerDropdown';
import usePlateMapDisplayAttributeState from 'samples/plate-maps/hooks/usePlateMapDisplayAttributeState';
import {
  DisplayLayerMetadata,
  PlateMap,
  ReagentAssignmentGroup,
  REAGENTS_ATTRIBUTE_SENTINEL,
  WELL_ROLE_ATTRIBUTE_SENTINEL,
} from 'samples/plate-maps/PlateMapConstants';
import PlateMapLegend from 'samples/plate-maps/PlateMapLegend';
import {
  createAssignmentGroupsForAttribute,
  getContentConcentrationOptions,
  getDisplayLayerAttributeKey,
  getWellPositionFromIndex,
} from 'samples/plate-maps/PlateMapUtil';
import PlateMapView from 'samples/plate-maps/PlateMapView';
import {getCellsAsIndices} from 'samples/SampleUtil';
import useAsyncState, {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import usePrevious from 'ui-library/hooks/usePrevious';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';

interface GridVisualizationProps {
  grid: FullyAccessibleGrid;
  selectedCells: Array<AccessibleGridCell>;
  onSelectCells: (selectedCells: Array<AccessibleGridCell>) => void;
  deselectWellsOnClickRefs?: Array<RefObject<HTMLElement>>;
}

async function getMeasurementLayerDropdownOptions(plateMap: PlateMap): Promise<Array<DisplayLayerMetadata>> {
  const assignmentGroups = await createAssignmentGroupsForAttribute(plateMap, REAGENTS_ATTRIBUTE_SENTINEL);
  return getContentConcentrationOptions(assignmentGroups as {[id: string]: ReagentAssignmentGroup});
}

/**
 * Displays an existing grid entity as a plate map and renders different layers of information
 * about the plate map.
 */
export default function GridVisualization({
  grid,
  selectedCells,
  onSelectCells,
  deselectWellsOnClickRefs = [],
}: GridVisualizationProps): JSX.Element {
  const {plateMap, isPending} = usePlateMapFromGrid(grid);
  const previousPlateMap = usePrevious(plateMap);
  const {assignmentGroupsPerAttribute, selectedAttribute, setSelectedAttribute, displayLayer} =
    usePlateMapDisplayAttributeState(plateMap, REAGENTS_ATTRIBUTE_SENTINEL, true);
  const containerRef = useRef<HTMLDivElement>(null);

  const {currentState, makeCall} = useAsyncState({
    makeCall: getMeasurementLayerDropdownOptions,
    keepOnlyLatest: true,
  });

  useEffect(() => {
    if (!isPending && !isEqual(previousPlateMap, plateMap)) {
      makeCall(plateMap);
    }
  }, [makeCall, plateMap, isPending, previousPlateMap]);

  return (
    <div ref={containerRef}>
      <div className={css(styles.layerDropdown)}>
        {currentState.status === AsyncStatus.SUCCESS ? (
          <PlateMapDisplayLayerDropdown
            containerSchemaId={grid.storableSchema.containerSchemaId}
            additionalAttributeOptions={[REAGENTS_ATTRIBUTE_SENTINEL, WELL_ROLE_ATTRIBUTE_SENTINEL]}
            selectedAttribute={selectedAttribute}
            setSelectedAttribute={setSelectedAttribute}
            measurementLayersOptions={currentState.data}
          />
        ) : (
          <HorizontalThrobber />
        )}
      </div>
      <PlateMapView
        plateMap={plateMap}
        selectedWellIndices={new Set(getCellsAsIndices(selectedCells, grid))}
        setSelectedWellIndices={(indices: Set<number>): void => {
          const newSelectedCells = [...indices]
            .map((index) => {
              const {row, column} = getWellPositionFromIndex(index, plateMap.size);
              return grid.cells[row][column];
            })
            .filter((cell): cell is AccessibleGridCell => isAccessibleGridCell(cell));
          onSelectCells(newSelectedCells);
        }}
        assignmentGroupsById={
          assignmentGroupsPerAttribute[getDisplayLayerAttributeKey(selectedAttribute)] ?? {}
        }
        isViewOnly={false}
        displayLayer={displayLayer}
        previewedWellIndices={new Set()}
        attribute={selectedAttribute}
        deselectWellsOnClickRefs={[containerRef, ...deselectWellsOnClickRefs]}
        showFinalMeasurements={false}
      />
      <PlateMapLegend
        plateMap={plateMap}
        displayLayer={displayLayer}
        assignmentGroupsById={
          assignmentGroupsPerAttribute[getDisplayLayerAttributeKey(selectedAttribute)] ?? {}
        }
        attribute={selectedAttribute}
        groupedValidationErrors={{}}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  layerDropdown: {
    marginBottom: '16px',
    width: '300px',
  },
});
